import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { apiService } from '../services/api';
import HilltopBanner from '../components/HilltopBanner';
import {
  Zap,
  BarChart3,
  Compass,
  FlaskConical,
  Database,
  ArrowRight,
  Boxes,
  Calculator as CalcIcon,
} from 'lucide-react';

function HomePage() {
  const [calculators, setCalculators] = useState([]);
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [calcRes, artRes] = await Promise.all([
          apiService.getCalculators(),
          apiService.getArticles()
        ]);
        setCalculators(calcRes.data || []);
        setArticles(artRes.data || []);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand"></div>
      </div>
    );
  }

  const featuredCalc = calculators[0] || {
    name: 'Quantum Compute',
    description: 'Our most advanced engine capable of processing multi-variable thermodynamic simulations in real-time.',
    slug: 'quantum-compute'
  };
  const secondaryCalcs = calculators.slice(1, 5);

  return (
    <div className="w-full space-y-40 pb-20">
      {/* Hero Section */}
      <section className="grid lg:grid-cols-2 gap-20 items-center">
        <motion.div
          initial={{ opacity: 0, x: -20 }}
          whileInView={{ opacity: 1, x: 0 }}
          viewport={{ once: true }}
          className="space-y-8"
        >
          <div className="inline-flex items-center gap-2 glass-pill text-brand border-brand/20">
            <span className="w-1.5 h-1.5 rounded-full bg-brand animate-pulse" />
            SYSTEM LIVE: COMPUTE READY
          </div>
          <h1 className="text-5xl md:text-7xl font-bold text-white tracking-tight leading-[1.1]">
            Smarter Calculations. <br />
            <span className="text-brand text-glow">Brighter Decisions.</span>
          </h1>
          <p className="text-slate-400 text-lg max-w-lg leading-relaxed">
            The high-performance workspace for quantitative analysis and technical insights.
            Process complex datasets with the speed of a professional athlete.
          </p>
          <div className="flex flex-wrap gap-4 pt-4">
            <Link to="/calculators" className="bg-brand text-[#050805] px-8 py-3 rounded-full font-bold hover:scale-105 transition-transform glow-green focus:outline-none focus:ring-2 focus:ring-brand focus:ring-offset-2 focus:ring-offset-[#050805]">
              Calculators Workspace
            </Link>
            <Link to="/articles" className="bg-transparent text-brand border border-brand/30 px-8 py-3 rounded-full font-bold hover:bg-brand/5 transition-colors flex items-center gap-2 focus:outline-none focus:ring-2 focus:ring-brand/40">
              View Articles
            </Link>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          className="relative"
        >
          <div className="absolute -inset-20 bg-brand/10 blur-[100px] rounded-full pointer-events-none" />
          <CalculatorWidget />
        </motion.div>
      </section>

      {/* Featured Engines */}
      <section>
        <div className="flex items-end justify-between mb-12 border-b border-white/5 pb-6">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Featured Calculators</h2>
            <p className="text-slate-500 text-sm">Architected for precision performance.</p>
          </div>
          <Link to="/calculators" className="text-brand text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:gap-3 transition-all">
            All Calculators<ArrowRight className="w-4 h-4" />
          </Link>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="md:col-span-2 glass rounded-3xl p-8 border-brand/20 relative overflow-hidden group focus-glow"
          >
            {/* Background Image for Featured Card */}
            <div className="absolute inset-0 z-0 opacity-20 group-hover:opacity-30 transition-opacity">
              <img src="/images/calc6-1_gen.png" alt="" className="w-full h-full object-cover" />
            </div>

            <Link to={`/calculators/${featuredCalc.slug}`} className="block relative z-10 focus:outline-none">
              <div className="flex items-start justify-between mb-12">
                <div className="space-y-4">
                  <div className="inline-flex items-center gap-2 text-[10px] font-bold text-brand uppercase tracking-widest">
                    <Zap className="w-3 h-3 fill-brand" />
                    Tier 1 Compute
                  </div>
                  <h3 className="text-4xl font-bold text-white">{featuredCalc.name}</h3>
                  <p className="text-slate-400 max-w-sm leading-relaxed">
                    {featuredCalc.description}
                  </p>
                </div>
                <div className="bg-brand/10 p-4 rounded-2xl border border-brand/20 backdrop-blur-md">
                  <Boxes className="w-12 h-12 text-brand" />
                </div>
              </div>

              <div className="flex gap-4">
                <div className="bg-brand/5 border border-brand/10 rounded-2xl px-6 py-4 backdrop-blur-md">
                  <div className="text-[10px] text-slate-500 uppercase font-bold mb-1">Latency</div>
                  <div className="text-xl font-mono text-brand">0.02ms</div>
                </div>
                <div className="bg-brand/5 border border-brand/10 rounded-2xl px-6 py-4 backdrop-blur-md">
                  <div className="text-[10px] text-slate-500 uppercase font-bold mb-1">Parallel</div>
                  <div className="text-xl font-mono text-brand">512-bit</div>
                </div>
              </div>
            </Link>

            <div className="absolute bottom-0 right-0 w-64 h-64 bg-gradient-to-tl from-brand/20 to-transparent blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 h-1 w-full bg-gradient-to-r from-transparent via-brand/30 to-transparent" />
          </motion.div>

          {secondaryCalcs.map((calc, idx) => (
            <EngineCard
              key={calc.id || idx}
              icon={idx === 0 ? <BarChart3 className="w-6 h-6 text-brand" /> :
                idx === 1 ? <Compass className="w-6 h-6 text-brand" /> :
                  idx === 2 ? <FlaskConical className="w-6 h-6 text-brand" /> :
                    <Database className="w-6 h-6 text-brand" />}
              title={calc.name}
              description={calc.description}
              slug={calc.slug}
              index={idx}
            />
          ))}
        </div>
      </section>

      {/* Hilltop Ad Banner */}
      <div className="flex justify-center py-2 border-y border-white/5 -my-10">
        <HilltopBanner />
      </div>

      {/* Insights Matrix */}
      <section>
        <div className="flex items-end justify-between mb-12 border-b border-white/5 pb-6">
          <div>
            <h2 className="text-3xl font-bold text-white mb-2">Insightful Articles</h2>
          </div>
          <div className="text-[10px] font-mono text-slate-600 uppercase tracking-[0.2em]">

            <Link to="/articles" className="text-brand text-xs font-bold uppercase tracking-widest flex items-center gap-2 hover:gap-3 transition-all">
              All ARTICLES // {articles.length.toString().padStart(3, '0')}<ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </div>

        <div className="grid md:grid-cols-3 gap-14">
          {articles.slice(0, 6).map((article, idx) => (
            <InsightCard
              key={article.id || idx}
              tag={article.category || "Technical"}
              image={article.image || ['/images/article_gen1.png', '/images/article_gen_v1.png', '/images/article_gen_v3.jpeg', '/images/article_gen_v4.png', '/images/article_gen_v5.png', '/images/article_gen_v6.png'][idx % 6]}
              title={article.title}
              description={article.excerpt || article.summary}
              slug={article.slug}
              index={idx}
              isFeatured={idx === 1}
            />
          ))}
        </div>
      </section>

      {/* CTA Section */}
      <motion.section
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        className="relative glass rounded-[40px] p-20 text-center overflow-hidden border-brand/20"
      >
        {/* Background Image for CTA */}
        <div className="absolute inset-0 z-0 opacity-30 pointer-events-none">
          <img src="/images/section_bg_gen.png" alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 bg-[#050805]/40" />
        </div>

        <div className="relative z-10 flex flex-col items-center max-w-2xl mx-auto space-y-8">
          <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight">
            Ready to Upgrade Your Intelligence?
          </h2>
          <p className="text-slate-400">
            Join over 15,000 analysts who rely on CalcBlog's high-performance infrastructure every day. Initialize your workspace now.
          </p>
          <Link to="/calculators" className="bg-brand text-[#050805] px-10 py-4 rounded-full font-bold hover:scale-105 transition-transform glow-green">
            Start Now
          </Link>
        </div>

        <div className="absolute top-0 left-0 w-full h-full opacity-20 pointer-events-none z-0">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] border border-brand/20 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] border border-brand/10 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[400px] h-[400px] border border-brand/5 rounded-full" />
        </div>
      </motion.section>
    </div>
  );
}

function CalculatorWidget() {
  const [display] = useState('1,240.45');

  return (
    <div className="glass rounded-3xl p-8 glow-green relative overflow-hidden group">
      <div className="flex items-center justify-between mb-8">
        <div className="text-[10px] font-mono text-slate-500 uppercase tracking-widest">MINI_CALC_V2.0</div>
        <CalcIcon className="w-4 h-4 text-brand opacity-50" />
      </div>

      <div className="bg-black/60 rounded-xl p-6 border border-white/5 mb-6 text-right relative overflow-hidden">
        <div className="absolute top-2 left-4 text-[8px] font-mono text-brand/50 uppercase">QUANTUM_LOAD: 42.8ms</div>
        <div className="text-4xl md:text-5xl font-mono text-white tracking-tighter">
          {display}
        </div>
      </div>

      <div className="grid grid-cols-4 gap-3">
        {['7', '8', '9', '/'].map(btn => (
          <CalcButton key={btn} label={btn} />
        ))}
        {['4', '5', '6', '*'].map(btn => (
          <CalcButton key={btn} label={btn} />
        ))}
        <button className="col-span-2 bg-brand/80 hover:bg-brand text-[#050805] py-4 rounded-xl font-bold transition-all text-sm uppercase tracking-wider">
          Execute
        </button>
        <CalcButton label="0" />
        <CalcButton label="+" variant="operator" />
      </div>

      <div className="absolute top-0 right-0 w-32 h-32 bg-gradient-to-br from-brand/5 to-transparent -rotate-45 pointer-events-none" />
    </div>
  );
}

function CalcButton({ label, variant = 'default' }) {
  return (
    <button className={`
      py-4 rounded-xl font-mono text-sm transition-all border
      ${variant === 'operator'
        ? 'bg-brand/10 border-brand/20 text-brand hover:bg-brand/20'
        : 'bg-white/5 border-white/10 text-white hover:bg-white/10'}
    `}>
      {label}
    </button>
  );
}

function EngineCard({ icon, title, description, slug, index }) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="glass rounded-3xl p-8 hover:border-brand/30 group cursor-pointer relative overflow-hidden focus-glow"
    >
      <Link to={`/calculators/${slug}`} className="block relative z-10 focus:outline-none">
        {/* Header Image for EngineCard */}
        <div className="relative aspect-[16/7] rounded-xl overflow-hidden border border-white/5 mb-6">
          <img
            src={['/images/calc3_gen.jpeg', '/images/calc6_gen.jpeg', '/images/calc5_gen.png', '/images/calc7_gen.jpeg'][index % 4]}
            alt={title}
            className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500 opacity-60 group-hover:opacity-100"
          />
        </div>

        <div className="mb-4">{icon}</div>
        <h3 className="text-xl font-bold text-white mb-2">{title}</h3>
        <p className="text-slate-400 text-xs leading-relaxed mb-6 line-clamp-2">
          {description}
        </p>
        <div className="w-full py-2.5 bg-white/5 border border-white/10 rounded-xl text-slate-400 text-[10px] font-bold uppercase tracking-widest hover:bg-brand/10 hover:text-brand hover:border-brand/20 transition-all text-center">
          Launch Calculator
        </div>
      </Link>

      <div className="absolute top-0 right-0 p-4 opacity-0 group-hover:opacity-100 transition-opacity">
        <ArrowRight className="w-4 h-4 text-brand" />
      </div>
    </motion.div>
  );
}

function InsightCard({ tag, image, title, description, slug, index, isFeatured = false }) {
  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      whileInView={{ opacity: 1, scale: 1 }}
      viewport={{ once: true }}
      transition={{ delay: index * 0.1 }}
      className="space-y-4 group cursor-pointer p-4 -m-4 rounded-3xl focus-glow"
    >
      <Link to={`/articles/${slug}`} className="block focus:outline-none">
        <div className="relative aspect-[16/10] rounded-2xl overflow-hidden border border-white/5">
          <img src={image} alt={title} className="w-full h-full object-cover group-hover:scale-105 transition-all duration-500" />
          <div className="absolute top-4 left-4 bg-brand/80 text-[#050805] px-2 py-0.5 rounded text-[8px] font-bold uppercase tracking-widest">
            {tag}
          </div>
          {isFeatured && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-[2px]">
              <div className="text-white font-bold tracking-[0.3em] text-xs uppercase border-y border-white/20 py-2 px-8">
                SAFE WORK
              </div>
            </div>
          )}
        </div>
        <div className="space-y-2 mt-4">
          <h3 className="text-sm font-bold text-white group-hover:text-brand transition-colors line-clamp-2">{title}</h3>
          <p className="text-[11px] text-slate-500 leading-relaxed font-medium line-clamp-2">
            {description}
          </p>
        </div>
      </Link>
    </motion.div>
  );
}

export default HomePage;
